import { useState, useEffect } from 'react';
import { Plus, Package, Search, Grid, List } from 'lucide-react';
import { getMyProducts, getProductTypes, deleteProduct, PRODUCT_STATUS } from '../../services/productsService';
import { useLanguage } from '../../context/LanguageContext';
import ProductCard from './ProductCard';
import ProductForm from './ProductForm';
import ProductDetail from './ProductDetail';

/**
 * Products Page Component
 * Main page for managing user's products
 */
const ProductsPage = () => {
    const { language, t } = useLanguage();
    const tp = t.products || {};
    const userId = parseInt(localStorage.getItem('userId'), 10);

    // State
    const [products, setProducts] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('grid'); // 'grid' or 'list'
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');

    // Modal states
    const [showForm, setShowForm] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);

    // Load data
    useEffect(() => {
        if (userId) {
            loadData();
        }
    }, [userId, language]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [productsData, typesData] = await Promise.all([
                getMyProducts(userId, language),
                getProductTypes(language)
            ]);
            setProducts(productsData);
            setProductTypes(typesData);
        } catch (error) {
            console.error('Error loading products data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter products
    const filteredProducts = products.filter(product => {
        const matchesSearch = !searchTerm ||
            product.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.description?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !filterStatus || product.status === filterStatus;

        const typeId = product.typeId || product.typeid || product.TYPEID;
        const matchesType = !filterType || typeId === parseInt(filterType);

        return matchesSearch && matchesStatus && matchesType;
    });

    // Handlers
    const handleAddProduct = () => {
        setSelectedProduct(null);
        setShowForm(true);
    };

    const handleEditProduct = (product) => {
        setSelectedProduct(product);
        setShowForm(true);
    };

    const handleViewProduct = (product) => {
        setSelectedProduct(product);
        setShowDetail(true);
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!productToDelete) return;

        const productUuid = productToDelete.productUuid || productToDelete.productuuid || productToDelete.PRODUCTUUID;
        const result = await deleteProduct(productUuid, userId);

        if (result.success) {
            setProducts(products.filter(p => {
                const pUuid = p.productUuid || p.productuuid || p.PRODUCTUUID;
                return pUuid !== productUuid;
            }));
        }

        setShowDeleteConfirm(false);
        setProductToDelete(null);
    };

    const handleFormSuccess = () => {
        setShowForm(false);
        setSelectedProduct(null);
        loadData();
    };

    const handleFormClose = () => {
        setShowForm(false);
        setSelectedProduct(null);
    };

    const handleDetailClose = () => {
        setShowDetail(false);
        setSelectedProduct(null);
    };

    const handleEditFromDetail = () => {
        setShowDetail(false);
        setShowForm(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cabaza-purple"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{tp.title}</h1>
                <button
                    onClick={handleAddProduct}
                    className="flex items-center gap-2 px-4 py-2 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-purple/90 transition-colors"
                >
                    <Plus className="w-5 h-5" />
                    {tp.addProduct}
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {/* Search */}
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={tp.search}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple/20 focus:border-cabaza-purple"
                    />
                </div>

                {/* Status filter */}
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple/20 focus:border-cabaza-purple bg-white"
                >
                    <option value="">{tp.allStatuses}</option>
                    <option value={PRODUCT_STATUS.ACTIVE}>{tp.active}</option>
                    <option value={PRODUCT_STATUS.SOLD}>{tp.sold}</option>
                    <option value={PRODUCT_STATUS.RESERVED}>{tp.reserved}</option>
                    <option value={PRODUCT_STATUS.INACTIVE}>{tp.inactive}</option>
                </select>

                {/* Type filter */}
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple/20 focus:border-cabaza-purple bg-white"
                >
                    <option value="">{tp.allCategories}</option>
                    {productTypes.map(type => {
                        const typeId = type.id || type.ID;
                        const typeName = type.name || type.NAME;
                        return (
                            <option key={typeId} value={typeId}>{typeName}</option>
                        );
                    })}
                </select>

                {/* View toggle */}
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                    <button
                        onClick={() => setView('grid')}
                        className={`p-2 ${view === 'grid' ? 'bg-cabaza-purple text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Grid className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setView('list')}
                        className={`p-2 ${view === 'list' ? 'bg-cabaza-purple text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                        <List className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Products grid/list */}
            {filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Package className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tp.noProducts}</h3>
                    <p className="text-gray-500 mb-6">{tp.noProductsDesc}</p>
                    <button
                        onClick={handleAddProduct}
                        className="flex items-center gap-2 px-4 py-2 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-purple/90 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        {tp.addProduct}
                    </button>
                </div>
            ) : (
                <div className={view === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                    : 'flex flex-col gap-4'
                }>
                    {filteredProducts.map(product => {
                        const productUuid = product.productUuid || product.productuuid || product.PRODUCTUUID;
                        return (
                            <ProductCard
                                key={productUuid}
                                product={product}
                                language={language}
                                isOwner={true}
                                onView={handleViewProduct}
                                onEdit={handleEditProduct}
                                onDelete={handleDeleteClick}
                            />
                        );
                    })}
                </div>
            )}

            {/* Product Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <ProductForm
                            product={selectedProduct}
                            onSuccess={handleFormSuccess}
                            onCancel={handleFormClose}
                        />
                    </div>
                </div>
            )}

            {/* Product Detail Modal */}
            {showDetail && selectedProduct && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <ProductDetail
                            product={selectedProduct}
                            isOwner={true}
                            onClose={handleDetailClose}
                            onEdit={handleEditFromDetail}
                        />
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{tp.deleteConfirm}</h3>
                        <p className="text-gray-600 mb-6">{tp.deleteMessage}</p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setProductToDelete(null);
                                }}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                {tp.cancel}
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                            >
                                {tp.delete}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductsPage;
